import {
    postConnectRequest,
    postAnswerRequest,
    postLocalIceCandidates,
    postRemoteIceCandidates,
    postDisconnectRequest,
    IceServer,
} from './webrtc';
import { DeviceId, StreamIndex, UtcTime } from 'types';
import { ApiError } from './fetch';

const _window: any = window; // tslint:disable-line:no-any
let supportsWebRTC = true;
try {
    RTCPeerConnection = _window.RTCPeerConnection || _window.mozRTCPeerConnection || _window.webkitRTCPeerConnection;
    RTCSessionDescription = _window.RTCSessionDescription || _window.mozRTCSessionDescription;
    RTCIceCandidate = _window.RTCIceCandidate || _window.mozRTCIceCandidate;
}
catch (err) {
    // IE fails on the lines above
    supportsWebRTC = false;
}

function trace(message: string): void {
    console.log(message);
}

function error(message: string): void {
    console.error(message);
}

export type FrameAndTime = {
    frame: string;
    utcTime: UtcTime;
};

export class WebRtcStreamer {
    videoElement: HTMLVideoElement | null;
    pc: RTCPeerConnection | null;
    mediaConstraints: object;
    peerId: string;
    deviceId: DeviceId | null;
    streamIndex: StreamIndex | null;
    utcStreamBaseTime: UtcTime | null; // When did the current stream start playing (relative to the stream)
    utcClientBaseTime: UtcTime | null; // When did the current stream start playing (relative to the client)
    isPlaying: boolean;

    constructor() {
        this.pc = null;
        this.videoElement = null;
        this.peerId = '';
        this.utcStreamBaseTime = null;
        this.utcClientBaseTime = null;
        this.isPlaying = false;

        this.mediaConstraints = {};
        if (navigator.userAgent.indexOf('Firefox') > 0) {
            this.mediaConstraints = {
                offerToReceiveVideo: true,
                offerToReceiveAudio: true,
            };
        }
        else {
            this.mediaConstraints = {
                mandatory: {
                    OfferToReceiveVideo: true,
                    OfferToReceiveAudio: true,
                },
            };
        }
    }
    
    private createPeerConnection(config: RTCConfiguration): RTCPeerConnection {
        trace(`createPeerConnection with ${JSON.stringify(config)}`);
        const pc = new RTCPeerConnection(config);

        pc.onicecandidate = e => {
            this.onLocalIceCandidate(e);
        };

        pc.onsignalingstatechange = (e: any) => { // tslint:disable-line:no-any // The node type definitions are incomplete
            trace(`Signaling State Change: ${e.target.signalingState}`);
        };

        pc.oniceconnectionstatechange = (e: any) => { // tslint:disable-line:no-any // The node type definitions are incomplete
            trace(`ICE Connection State Change: ${e.target.iceConnectionState}`);
        };

        // TODO: switch to using track events because stream events are obsolete

        (pc as any).onaddstream = (e: any) => {
            if (e.stream) {
                this.onAddStream(e.stream);
            }
        };

        (pc as any).onremovestream = (e: any) => {
            if (e.stream) {
                this.onRemoveStream(e.stream);
            }
        };

        return pc;
    }
    
    private onLocalIceCandidate(event: RTCPeerConnectionIceEvent) {
        const { candidate } = event;
        if (candidate) {
            trace(`Sending ICE candidate: ${JSON.stringify(candidate)}`);
            try {
                postLocalIceCandidates(this.deviceId!, this.peerId, [ candidate ]);
            }
            catch (err) {
                error(`Failed to send ICE candidates: ${err.message}`);
            }
        }
        else {
            trace('End of candidates.');
        }
    }

    private onAddStream(stream: MediaStream) {
        trace(`Remote stream added: ${stream.id}`);
        stream.onaddtrack = e => this.onAddTrack(e.track);
        stream.onremovetrack = e => this.onRemoveTrack(e.track);
        if (!this.videoElement) {
            error('video element is null');
            throw new ApiError('341'); // Operation failed
        }
        this.videoElement.style.background = 'initial';
        this.isPlaying = true;
        this.utcClientBaseTime = Date.now();
        this.videoElement.srcObject = stream;
        (async ve => {
            try {
                await ve.play();
            }
            catch (err) {
                error(`Failed to play video ${stream.id}: ${err.message}`);
            }
        })(this.videoElement);
    }

    private onAddTrack(track: MediaStreamTrack) {
        trace(`Track added: ${track.id}`);
    }

    private onRemoveTrack(track: MediaStreamTrack) {
        trace(`Track removed: ${track.id}`);
    }

    private onRemoveStream(stream: MediaStream) {
        trace(`Remote stream removed: ${stream.id}`);
        if (this.videoElement) {
            this.videoElement.srcObject = null;
            this.isPlaying = false;
        }
    }

    private async getRemoteIceCandidates() {
        if (!this.pc || !this.deviceId) return;

        const candidatesJson = await postRemoteIceCandidates(this.deviceId, this.peerId);
        const candidates = JSON.parse(candidatesJson) as RTCIceCandidateInit[];

        trace(`Received ${candidates && candidates.length || 0} remote ICE candidates`);
        if (candidates && !!this.pc) {
            for (let i = 0; i < candidates.length; i++) {
                const candidate = new RTCIceCandidate(candidates[i]);
                
                trace(`Adding remote ICE candidate: ${JSON.stringify(candidate)}`);
                try {
                    await this.pc.addIceCandidate(candidate);
                }
                catch (err) {
                    error(`Failed to receive candidate: ${err.message}`);
                    throw err;
                }
            }
        }
    }

    private async generateAnswer() {
        if (!this.pc) return;
        
        const answer = await this.pc.createAnswer(this.mediaConstraints as any); // tslint:disable-line:no-any // The node type definitions are incomplete
        trace(`Create answer: ${JSON.stringify(answer)}`);
        await this.pc.setLocalDescription(new RTCSessionDescription(answer as any) as any); // tslint:disable-line:no-any // The node type definitions are incomplete
        return this.pc.localDescription;
    }

    private async negotiate(offer: string): Promise<void> {
        if (this.pc === null) throw new Error('PeerConnection is null');
        if (this.deviceId === null) throw new Error('deviceId is null');

        trace(`offer: ${offer}`);
        await this.pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(offer)) as any); // tslint:disable-line:no-any // The node type definitions are incomplete

        const answer = await this.generateAnswer();
        await postAnswerRequest(this.deviceId, this.peerId, JSON.stringify(answer));
    }


    //
    //  Public Methods
    //

    public async connect(videoElement: HTMLVideoElement | null, deviceId: DeviceId, streamIndex: StreamIndex, utcTime: UtcTime = 0): Promise<UtcTime> {
        if (!supportsWebRTC) {
            throw new ApiError('1877'); // Not supported.
        }

        if (!videoElement) {
            error('video element is null');
            throw new ApiError('341'); // Operation failed
        }
        
        try {
            const { peerId, offer, iceServers } = await postConnectRequest(deviceId, streamIndex, utcTime);
            if (!videoElement) {
                error('disconnected before connect finished (video element is null)');
                throw new ApiError('341'); // Operation failed
            }

            this.peerId = peerId;
            this.deviceId = deviceId;
            this.streamIndex = streamIndex;
            this.videoElement = videoElement;
            const config = this.makeConfig(iceServers);
            this.pc = this.createPeerConnection(config);

            await this.negotiate(offer);
            if (!this.isConnected()) {
                error('disconnected before connect finished (after negotiate)');
                throw new ApiError('341'); // Operation failed
            }

            await this.getRemoteIceCandidates();
            if (!this.isConnected()) {
                error('disconnected before connect finished (after getRemoteIceCandidates)');
                throw new ApiError('341'); // Operation failed
            }

            utcTime = utcTime || Date.now();
            this.utcStreamBaseTime = utcTime;
            return utcTime;
        }
        catch (err) {
            await this.disconnect();
            error(`Failed to connect to camera ${deviceId} stream ${streamIndex}: ${err.message}`);
            throw new ApiError('6.9622.3792'); // Failed to connect
        }
    }

    public disconnect(): UtcTime {
        this.streamIndex = null;
        this.isPlaying = false;

        // Calculate what we think the current video time is
        // TODO: would like to get exact time stamp (perhaps open a data channel at the beginning to receive info like timestamp and decorations?)
        const delta = this.utcClientBaseTime ? Date.now() - this.utcClientBaseTime : 0;
        const utcStopTime = (this.utcStreamBaseTime || Date.now()) + delta;

        if (this.videoElement) {
            const stream = this.videoElement.srcObject;
            if (stream instanceof MediaStream) {
                stream.getTracks().forEach(track => {
                    track.stop();
                    stream.removeTrack(track);
                });
            }
            this.videoElement.srcObject = null;
            this.videoElement = null;
        }

        if (this.pc) {
            trace('disconnect');
            try {
                this.pc.close();

                // NOTE: This is an asynchronous call and we are not waiting for it to complete
                //       so that we can return the stop time as quickly as possible.
                postDisconnectRequest(this.deviceId!, this.peerId);
            }
            catch (e) {
                error(`Failure closing peer connection: ${e.message}`);
            }
            this.pc = null;
            this.deviceId = null;
            this.peerId = '';
        }

        this.utcClientBaseTime = null;
        this.utcStreamBaseTime = null;

        return utcStopTime;
    }

    public isConnected() {
        return this.pc !== null;
    }

    private makeConfig(apiIceServers: ReadonlyArray<IceServer>): RTCConfiguration {
        const iceServers = apiIceServers.map(({ type, url, username, password }) => ({
            urls: [
                `${type}:${url}`,
            ],
            username,
            credential: password,
        }));

        return {
            iceServers,
        };
    }
}



// WEBPACK FOOTER //
// ./src/api/webRtcStreamer.ts